import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorStateService } from '../services/common/error-state.service';
import { isDevPortalUrl } from '../utils/dev-portal-request.util';
import { HttpStatus } from '../utils/http-status.util';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../utils/http-context-tokens.util';

export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const errorStateService = inject(ErrorStateService);
  const skipRedirect = req.context.get(SKIP_GLOBAL_ERROR_REDIRECT);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if it's a network error (backend is down)
      if (error.status === HttpStatus.NetworkError) {
        // Network error - backend is unreachable
        console.error('Network Error: Backend service is unreachable', error.url);
        // DEBUG: Log full error details to diagnose file upload failures
        console.error('🔴 [Network Error Debug] Full error:', {
          status: error.status,
          statusText: error.statusText,
          url: error.url,
          message: error.message,
          errorType: error.error?.constructor?.name,
          errorDetail: error.error instanceof ProgressEvent ? {
            type: error.error.type,
            loaded: error.error.loaded,
            total: error.error.total,
            lengthComputable: error.error.lengthComputable,
          } : error.error,
          requestMethod: req.method,
          requestUrl: req.url,
          requestHeaders: req.headers.keys(),
          hasBody: !!req.body,
          bodyType: req.body?.constructor?.name,
        });

        // Check if this is an auth-related request - don't redirect for these
        // This allows the login page to be shown even when backend is down
        const isAuthRequest = req.url.includes('/api/auth/');

        // See SKIP_GLOBAL_ERROR_REDIRECT's own docblock: a request that opts
        // out already has its own NET toast (SheetLoadTraceService or
        // page-load-failure.util.ts) — redirecting here would destroy that
        // page before its toast could render, which is exactly the race this
        // flag exists to lose on purpose.
        if (!isAuthRequest && !skipRedirect) {
          errorStateService.setError({
            type: 'network',
            message: 'Unable to connect to the server. Please check your internet connection or try again later.',
            timestamp: new Date(),
          });

          // Navigate to error page for non-auth routes when backend is down
          router.navigate(['/error'], {
            queryParams: { type: 'network' },
            skipLocationChange: false
          });
        }
      } else if (error.status === HttpStatus.ServiceUnavailable) {
        // Service Unavailable
        console.error('Service Unavailable: Backend service is down', error.url);
        // The static /error page is kept for the developer portal only. A
        // customer call reports its 5xx itself (the `SRV` diagnostic toast on
        // page loads, the caller's own handling on writes), exactly as it
        // already does for a 500 — this branch used to be the one 5xx that
        // navigated away instead.
        if (!skipRedirect && isDevPortalUrl(req.url)) {
          errorStateService.setError({
            type: 'service-unavailable',
            message: 'The service is temporarily unavailable. Please try again later.',
            timestamp: new Date(),
          });
          router.navigate(['/error'], {
            queryParams: { type: 'service-unavailable' },
            skipLocationChange: true
          });
        }
      } else if (error.status === HttpStatus.InternalServerError) {
        // Internal Server Error
        console.error('Server Error:', error.url);
        errorStateService.setError({
          type: 'server-error',
          message: 'An unexpected error occurred. Please try again later.',
          timestamp: new Date(),
        });
      }

      // Re-throw the error for individual handlers to process if needed
      return throwError(() => error);
    })
  );
};
